import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { CancelButton, DeleteButton } from '../../../components/mui/Buttons/Buttons';
import { revokeSkillxCommonBadge } from '../../../redux/features/skillxSlice';

/**
 * Take a common badge back.
 *
 * WHY THIS TAKES A LIST RATHER THAN AN AWARD. A learner can hold the same badge
 * more than once — three separate months of perfect attendance are three rows —
 * and the roster collapses those into a single `×3` chip because that is the
 * fact a mentor wants to see. But revoking acts on ONE row, so a dialog handed
 * only the chip would silently revoke whichever award happened to sort first
 * and leave the mentor unable to say which. When there is more than one, this
 * asks.
 *
 * The default is the most recent, because "I just awarded that by mistake" is
 * overwhelmingly the reason anyone opens this.
 *
 * REVOKING IS NOT A DELETE. The server flips `award_status` to 2 and appends
 * the reason to the note, keeping the row. A badge taken back is something that
 * happened to a learner, and a mentor who removes one by mistake should be able
 * to see that they did.
 */
export default function RevokeBadgeDialog({ badgeName, awards = [], learner, onRevoked }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [awardId, setAwardId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Newest first — the same order the server returns, restated here so the
  // default does not depend on it.
  const ordered = useMemo(
    () =>
      [...awards].sort(
        (a, b) => new Date(b.awardedOn || 0) - new Date(a.awardedOn || 0),
      ),
    [awards],
  );

  useEffect(() => {
    if (open) {
      setAwardId(ordered[0]?.awardId ?? '');
      setReason('');
    }
  }, [open, ordered]);

  const selected = ordered.find((a) => a.awardId === awardId) || ordered[0] || null;

  const submit = () => {
    if (!selected) return;

    setBusy(true);
    dispatch(revokeSkillxCommonBadge({ awardId: selected.awardId, reason: reason.trim() }))
      .then((res) => {
        const payload = res.payload;

        if (payload?.status === 'success') {
          enqueueSnackbar(payload.message || 'Badge revoked', { variant: 'success' });
          setOpen(false);
          setReason('');
          onRevoked?.();
          return;
        }

        // apiRequest resolves rather than rejecting, so a dropped connection
        // arrives here with no status at all.
        enqueueSnackbar(payload?.message || 'Could not revoke the badge', {
          variant: 'error',
        });
      })
      .finally(() => setBusy(false));
  };

  const when = (award) =>
    award?.awardedOn ? new Date(award.awardedOn).toLocaleDateString() : 'date unknown';

  return (
    <GlobalDialog
      title="Revoke badge"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="xs"
      closeButton
      popupButton={
        <Tooltip title="Revoke">
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            sx={{ display: 'inline-flex', cursor: 'pointer', ml: 0.25 }}
          >
            <Icon icon="solar:close-circle-linear" width={15} height={15} />
          </Box>
        </Tooltip>
      }
      content={
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: 14 }}>
            Take <b>{badgeName}</b> back from {learner?.name}? It disappears from their
            dashboard straight away.
          </Typography>

          {/* Only asked when it is a real question. One award needs no picker. */}
          {ordered.length > 1 && (
            <>
              <Alert severity="info" sx={{ fontSize: 13 }}>
                {learner?.name} holds this badge {ordered.length} times. Only the one you
                choose is revoked — the others stay.
              </Alert>

              <TextField
                select
                fullWidth
                size="small"
                label="Which award?"
                value={awardId}
                onChange={(e) => setAwardId(Number(e.target.value))}
              >
                {ordered.map((a, i) => (
                  <MenuItem key={a.awardId} value={a.awardId}>
                    {when(a)}
                    {i === 0 ? ' (most recent)' : ''} — {a.note || 'no reason recorded'}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          {selected?.note && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              Awarded {when(selected)}
              {selected.awardedBy ? ` by ${selected.awardedBy}` : ''} for: {selected.note}
            </Typography>
          )}

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            placeholder="Awarded to the wrong learner"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="Kept on the record — the award row stays, marked revoked."
          />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <CancelButton action={() => setOpen(false)}>Cancel</CancelButton>
            <DeleteButton action={submit} disabled={busy || !selected}>
              Revoke
            </DeleteButton>
          </Stack>
        </Stack>
      }
    />
  );
}
